import { Alert, Box, Skeleton, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { StatStrip } from "../../../components/UIToolkit/molecules/StatStrip";
import { useRecurringStartPreview } from "../hooks";

type Props = {
	departmentId: string | undefined;
	minStartDate: string;
};

const NOT_LOADED = "—";

function formatDate(isoDate: string | null): string {
	if (!isoDate) return NOT_LOADED;
	return DateTime.fromISO(isoDate).toFormat("MMM d, yyyy");
}

/**
 * Shown before a template is uploaded: the date from which the load will actually schedule new
 * shifts, so the onboarder can set "Min start date" instead of discovering the boundary after the
 * fact. Occurrences before that date fall inside the already-loaded schedule and are reconciled
 * against it rather than scheduled.
 */
export function RecurringStartPreviewPanel({ departmentId, minStartDate }: Props) {
	const { data: preview, isLoading, isError } = useRecurringStartPreview(departmentId, minStartDate || undefined, Boolean(departmentId));

	if (!departmentId) return null;

	if (isLoading) {
		return <Skeleton variant="rounded" height={72} sx={{ mb: 2 }} data-testid="recurring-start-preview-loading" />;
	}

	if (isError || !preview) {
		return (
			<Alert severity="warning" sx={{ mb: 2 }} data-testid="recurring-start-preview-error">
				Couldn't check where recurring shifts would start. The load will still run.
			</Alert>
		);
	}

	const hasLoadedShifts = preview.loaded_shifts_end !== null;

	return (
		<Box sx={{ mb: 2 }} data-testid="recurring-start-preview">
			<Typography variant="body2" sx={{ mb: 1 }}>
				{preview.live_creation_start ? (
					<>
						Recurring shifts will start being created on <strong>{formatDate(preview.live_creation_start)}</strong>.
					</>
				) : (
					<>Recurring shifts will start being created from each employee's own template start date.</>
				)}
			</Typography>
			<StatStrip
				dataTestId="recurring-start-preview-stats"
				items={[
					{
						label: "Shifts loaded",
						value: hasLoadedShifts ? `${formatDate(preview.loaded_shifts_start)} → ${formatDate(preview.loaded_shifts_end)}` : NOT_LOADED,
					},
					{ label: "Employees", value: preview.employees_in_department },
					{ label: "No shifts loaded", value: preview.employees_without_loaded_shifts, dataTestId: "recurring-start-preview-no-shifts" },
					{ label: "Partial coverage", value: preview.employees_with_partial_coverage, dataTestId: "recurring-start-preview-partial" },
				]}
			/>
			{preview.employees_without_loaded_shifts > 0 || preview.employees_with_partial_coverage > 0 ? (
				<Typography variant="caption" sx={{ display: "block", mt: 1 }}>
					Employees with no shifts loaded, or loaded only through an earlier date, get their shifts created from their own template start date — their schedule
					is not reconciled against the department's loaded range.
				</Typography>
			) : null}
		</Box>
	);
}
